import { SALES_API_BASE_URL } from "app/pages/sales-intelligence/constants/api";
import type { IFetchService } from "app/services/fetchService";

import type {
    CreateTeamRequest,
    CreateTeamResponse,
    TeamListItem,
    TeamUser,
    UpdateTeamFiltersRequest,
    UpdateTeamLeadScoringCriteriaRequest,
    UpdateTeamMembersRequest,
    UpdateTeamRequest,
} from "../../types/teams.types";

export type FetchTeamsParams = {
    onlyMyTeams?: boolean;
};

export type TeamsApiService = {
    fetchTeams(params?: FetchTeamsParams): Promise<readonly TeamListItem[]>;
    fetchAccountUsers(): Promise<TeamUser[]>;
    createTeam(payload: CreateTeamRequest): Promise<CreateTeamResponse>;
    updateTeam(teamId: number, payload: UpdateTeamRequest): Promise<void>;
    updateTeamMembers(teamId: number, payload: UpdateTeamMembersRequest): Promise<void>;
    updateTeamFilters(teamId: number, payload: UpdateTeamFiltersRequest): Promise<void>;
    updateTeamLeadScoringCriteria(teamId: number, payload: UpdateTeamLeadScoringCriteriaRequest): Promise<void>;
    deleteTeam(teamId: number): Promise<void>;
};

const TEAMS_BASE_URL = `${SALES_API_BASE_URL}/teams`;

const createTeamsApiService = (fetchService: Readonly<IFetchService>): TeamsApiService => {
    return {
        fetchTeams(params) {
            return fetchService.get<TeamListItem[]>(
                TEAMS_BASE_URL,
                params?.onlyMyTeams ? { onlyMyTeams: true } : undefined,
            );
        },
        fetchAccountUsers() {
            return fetchService.get<TeamUser[]>(`${TEAMS_BASE_URL}/users`);
        },
        createTeam(payload) {
            return fetchService.post<CreateTeamResponse>(TEAMS_BASE_URL, payload);
        },
        updateTeam(teamId, payload) {
            return fetchService.put<void>(`${TEAMS_BASE_URL}/${teamId}`, payload);
        },
        updateTeamMembers(teamId, payload) {
            return fetchService.put<void>(`${TEAMS_BASE_URL}/${teamId}/members`, payload);
        },
        updateTeamFilters(teamId, payload) {
            const { queryFilters } = payload;
            return fetchService.put<void>(`${TEAMS_BASE_URL}/${teamId}/filters`, {
                queryFilters: { ...queryFilters, leadsFilters: queryFilters.leadsFilters ?? {} },
            });
        },
        updateTeamLeadScoringCriteria(teamId, payload) {
            return fetchService.put<void>(`${TEAMS_BASE_URL}/${teamId}/lead-scoring-criteria`, payload);
        },
        deleteTeam(teamId) {
            return fetchService.delete<void>(`${TEAMS_BASE_URL}/${teamId}`);
        },
    };
};

export default createTeamsApiService;
